import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { NC_COUNTIES } from "@/lib/ncCounties";
import { FolderPlus, Briefcase, ArrowRight, FileText, CheckCircle2, Plus } from "lucide-react";

export const MATTER_TYPES = [
  "General", "Divorce / Separation", "Child Custody", "Child Support",
  "Domestic Violence", "Eviction / Landlord-Tenant", "Small Claims",
  "Estate / Probate", "Name Change", "Guardianship", "Expunction",
];

export default function Matters() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [scope, setScope] = useState("firm");
  const inFirm = !!user?.firm_id;
  const [name, setName] = useState("");
  const [matterType, setMatterType] = useState("General");
  const [county, setCounty] = useState("");
  const subscribed = user && (["active", "trialing"].includes(user.subscription_status) || user.role === "admin");

  const load = () => api.get("/matters", { params: { scope } }).then(({ data }) => setMatters(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [scope]);

  const create = async () => {
    if (!name.trim()) { toast.error(t("matters.nameRequired")); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/matters", { name: name.trim(), matter_type: matterType, county });
      setMatters((m) => [data, ...m]);
      setOpen(false); setName(""); setMatterType("General"); setCounty("");
      toast.success(t("matters.created"));
    } catch { toast.error(t("matters.createFail")); }
    setCreating(false);
  };

  return (
    <div>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-24 min-h-[60vh]">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
          <div>
            <p className="overline text-accent mb-2">{t("matters.eyebrow")}</p>
            <h1 className="font-serif text-5xl tracking-tighter">{t("matters.title")}</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-matter-btn" className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                <FolderPlus size={16} /> {t("matters.newMatter")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="new-matter-dialog">
              <DialogHeader><DialogTitle className="font-serif text-2xl">{t("matters.newMatter")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-sm">{t("matters.name")}</Label>
                  <Input data-testid="matter-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("matters.namePlaceholder")} className="rounded-sm mt-1.5" />
                </div>
                <div>
                  <Label className="text-sm">{t("matters.type")}</Label>
                  <Select value={matterType} onValueChange={setMatterType}>
                    <SelectTrigger data-testid="matter-type-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MATTER_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">{t("matters.county")}</Label>
                  <Select value={county} onValueChange={setCounty}>
                    <SelectTrigger data-testid="matter-county-select" className="rounded-sm mt-1.5"><SelectValue placeholder={t("matters.countyPlaceholder")} /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {NC_COUNTIES.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={creating} data-testid="create-matter-btn" className="rounded-sm bg-accent text-accent-foreground hover:bg-accent/90 gap-2">
                  <Plus size={16} /> {creating ? t("matters.creating") : t("matters.create")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-8">{t("matters.subtitle")}</p>

        {inFirm && (
          <div className="inline-flex border-2 border-black rounded-sm mb-8 overflow-hidden" data-testid="matters-scope">
            {[["firm", "Firm cases"], ["mine", "My cases"]].map(([v, label]) => (
              <button key={v} onClick={() => setScope(v)} data-testid={`scope-${v}`}
                className={`px-4 h-9 text-sm font-semibold transition-colors ${scope === v ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-muted-foreground">{t("matters.loading")}</p>
        ) : matters.length === 0 ? (
          <div className="grid-card p-12 text-center" data-testid="matters-empty">
            <Briefcase className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">{t("matters.empty")}</p>
            <Button onClick={() => setOpen(true)} className="rounded-sm gap-2 bg-accent text-accent-foreground"><FolderPlus size={16} /> {t("matters.newMatter")}</Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {matters.map((m) => (
              <Link key={m.id} to={`/matters/${m.id}`} data-testid={`matter-card-${m.id}`}
                className="grid-card p-6 group hover:-translate-y-1 transition-transform flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <Badge className="rounded-sm bg-primary">{m.matter_type}</Badge>
                  <Briefcase size={18} className="text-accent" />
                </div>
                <h3 className="font-serif text-xl leading-tight mb-2 flex-1 group-hover:text-accent transition-colors">{m.name}</h3>
                {m.county && <p className="text-xs text-muted-foreground mb-3">{m.county} County</p>}
                {m.created_by_name && <p className="text-[0.7rem] text-muted-foreground mb-3">Created by {m.created_by_name}</p>}
                <div className="flex items-center gap-4 text-sm text-muted-foreground border-t border-border/60 pt-3">
                  <span className="inline-flex items-center gap-1.5"><FileText size={14} /> {t("matters.forms", { count: m.form_count || 0 })}</span>
                  {m.filed_count > 0 && <span className="inline-flex items-center gap-1.5 text-emerald-700"><CheckCircle2 size={14} /> {m.filed_count}</span>}
                  <ArrowRight size={16} className="ml-auto group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
